import React, { useState, useEffect } from 'react';
import { Star, Check, X, Trash2, Eye, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('pending'); // 'pending', 'approved', 'all'

  useEffect(() => {
    fetchReviews();
  }, [activeFilter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      // Fetch all reviews (approved_only=false)
      const response = await axios.get(`${API}/reviews?approved_only=false`);
      
      let filteredReviews = response.data;
      if (activeFilter === 'pending') {
        filteredReviews = response.data.filter(r => !r.approved);
      } else if (activeFilter === 'approved') {
        filteredReviews = response.data.filter(r => r.approved);
      }
      
      setReviews(filteredReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}/approve`);
      toast.success('Review approved successfully!');
      fetchReviews();
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Failed to approve review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success('Review deleted successfully!');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}
          />
        ))}
      </div>
    );
  };

  const pendingCount = reviews.filter(r => !r.approved).length;
  const approvedCount = reviews.filter(r => r.approved).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Review Management</h2>
          <p className="text-gray-400 text-sm">
            Approve or reject customer reviews
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
              activeFilter === 'pending'
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Clock size={16} />
              <span>Pending</span>
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveFilter('approved')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
              activeFilter === 'approved'
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Check size={16} />
              <span>Approved</span>
              {approvedCount > 0 && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {approvedCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
              activeFilter === 'all'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Eye size={16} />
              <span>All</span>
            </div>
          </button>
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Loading reviews...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-12 text-center">
          <Star className="text-purple-400 mx-auto mb-4" size={48} />
          <p className="text-gray-400">
            {activeFilter === 'pending' && 'No pending reviews'}
            {activeFilter === 'approved' && 'No approved reviews yet'}
            {activeFilter === 'all' && 'No reviews yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.id}
              className={`bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border rounded-xl p-6 ${
                review.approved ? 'border-green-500/30' : 'border-orange-500/30'
              }`}
            >
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                {/* Review Content */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-white font-semibold">{review.name}</h3>
                        {renderStars(review.rating)}
                      </div>
                      <p className="text-sm text-gray-400">{review.email}</p>
                      {review.engineer_name && (
                        <p className="text-sm text-purple-300 mt-1">
                          Session with: {review.engineer_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {review.approved ? (
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                          Approved
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-semibold rounded-full border border-orange-500/30">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed">{review.review_text}</p>
                  
                  <p className="text-xs text-gray-500">
                    Submitted: {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2">
                  {!review.approved && (
                    <button
                      onClick={() => handleApprove(review.id)}
                      className="flex-1 lg:flex-none px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                    >
                      <Check size={16} />
                      <span>Approve</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(review.id)}
                    className="flex-1 lg:flex-none px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
